import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FolderKanban, Search, Building2 } from "lucide-react";
import { formatDate, PROJECT_STATUS_META } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

export default function ProjectsListPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const clientIdParam = params.get("client_id");
  const openNewParam = params.get("new") === "1";

  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(openNewParam);
  const [form, setForm] = useState({ client_id: clientIdParam || "", status: "em_andamento" });
  const navigate = useNavigate();
  const isAdmin = user?.role === "ADMIN";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/projects", { params: { ...(clientIdParam && { client_id: clientIdParam }), ...(q && { q }) } });
      setProjects(r.data);
    } catch { toast.error("Erro ao carregar projetos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load();  }, [clientIdParam]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t);  }, [q]);

  useEffect(() => {
    if (isAdmin) api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [isAdmin]);

  const save = async () => {
    try {
      const payload = { ...form };
      if (payload.due_date === "") delete payload.due_date;
      if (payload.start_date === "") delete payload.start_date;
      const r = await api.post("/projects", payload);
      toast.success("Projeto criado");
      setProjects((p) => [r.data, ...p]);
      setOpen(false);
      setForm({ client_id: clientIdParam || "", status: "em_andamento" });
      if (openNewParam) { params.delete("new"); setParams(params); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{isAdmin ? "Gestão" : "Meus"}</p>
          <h1 className="font-heading text-3xl font-bold mt-1">Projetos</h1>
        </div>
        {isAdmin && (
          <Button onClick={() => setOpen(true)} className="gap-2" data-testid="new-project-btn">
            <Plus size={16} /> Novo projeto
          </Button>
        )}
      </header>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Buscar por nome, código…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="projects-search-input" />
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
      ) : projects.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-sm text-muted-foreground">
          <FolderKanban className="mx-auto mb-3 text-muted-foreground/50" />
          Nenhum projeto encontrado.
        </CardContent></Card>
      ) : isAdmin ? (
        <div className="space-y-8" data-testid="projects-grouped-by-client">
          {groupByClient(projects, clients).map(({ client, items }) => (
            <section key={client.id} data-testid={`client-group-${client.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={15} className="text-muted-foreground" />
                {client.id === "unknown" ? (
                  <h2 className="font-heading font-semibold">{client.name}</h2>
                ) : (
                  <Link to={`/clients/${client.id}`} className="font-heading font-semibold hover:text-primary transition-colors" data-testid={`client-group-link-${client.id}`}>
                    {client.name}
                  </Link>
                )}
                {client.company && <span className="text-xs text-muted-foreground">— {client.company}</span>}
                <span className="text-xs text-muted-foreground">({items.length})</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((p) => <ProjectCard key={p.id} project={p} />)}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="client-projects-grid">
          {projects.map((p) => <ProjectCard key={p.id} project={p} />)}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Novo projeto</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs">Cliente *</Label>
              <select value={form.client_id || ""} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                className="h-10 w-full rounded-md border bg-background px-3 text-sm" data-testid="project-form-client">
                <option value="">Selecione…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name} {c.company ? `— ${c.company}` : ""}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-form-name" />
            </div>
            <div>
              <Label className="text-xs">Código</Label>
              <Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="project-form-code" />
            </div>
            <div>
              <Label className="text-xs">Responsável BAUEN</Label>
              <Input value={form.responsible || ""} onChange={(e) => setForm({ ...form, responsible: e.target.value })} data-testid="project-form-responsible" />
            </div>
            <div>
              <Label className="text-xs">Categoria</Label>
              <Input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="project-form-category" />
            </div>
            <div>
              <Label className="text-xs">Data início</Label>
              <Input type="date" value={form.start_date?.slice?.(0, 10) || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="project-form-start" />
            </div>
            <div>
              <Label className="text-xs">Prazo</Label>
              <Input type="date" value={form.due_date?.slice?.(0, 10) || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="project-form-due" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Descrição</Label>
              <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-form-description" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select value={form.status || "em_andamento"} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="h-10 w-full rounded-md border bg-background px-3 text-sm" data-testid="project-form-status">
                <option value="em_andamento">Em andamento</option>
                <option value="aguardando">Aguardando</option>
                <option value="finalizado">Finalizado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={!form.name || !form.client_id} data-testid="project-form-save">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function groupByClient(projects, clients) {
  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
  const groups = {};
  for (const p of projects) {
    const key = (p.client_id && clientMap[p.client_id]) ? p.client_id : "unknown";
    (groups[key] = groups[key] || []).push(p);
  }
  return Object.entries(groups)
    .map(([clientId, items]) => ({
      client: clientMap[clientId] || { id: "unknown", name: "Sem cliente vinculado" },
      items,
    }))
    .sort((a, b) => a.client.name.localeCompare(b.client.name));
}

function ProjectCard({ project: p }) {
  const meta = PROJECT_STATUS_META[p.status] || { label: p.status, color: "#64748B" };
  return (
    <Link to={`/projects/${p.id}`} data-testid={`project-item-${p.id}`}
      className="block card-lift bg-card border rounded-xl p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-heading font-semibold truncate">{p.name}</p>
          {p.code && <p className="text-xs font-mono text-muted-foreground mt-0.5">{p.code}</p>}
        </div>
        <span className="shrink-0 text-[11px] font-medium rounded-full px-2 py-0.5 border"
          style={{ color: meta.color, borderColor: `${meta.color}40`, backgroundColor: `${meta.color}15` }}>{meta.label}</span>
      </div>
      {p.description && <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{p.description}</p>}
      <div className="mt-4 pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
        <span>{p.responsible || "—"}</span>
        <span>Prazo: {formatDate(p.due_date)}</span>
      </div>
    </Link>
  );
}
